import Box from "@mui/material/Box";
import { Trans, useTranslation } from "react-i18next";
import List from "@mui/material/List";
import React, { useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import ListItemRadioButton from "../../../components/Common/ListItemRadioButton";
import {
  CheckoutCardContent,
  CheckoutCardList,
  CheckoutCardSubHeader,
} from "./CheckoutCard";
import FormikTextField from "../../../components/Common/FormikTextField";
import Button from "@mui/material/Button";
import { useAuth } from "../../providers/AuthProvider";
import { getYUPValidators } from "../../../components/yupvalidators";
import * as Yup from "yup";
import { useCheckout } from "../../providers/CheckoutProvider";
import { formatCurrency } from "../../../utils";
import makeStyles from "@mui/styles/makeStyles";
import { alpha } from "@mui/material/styles";

const useStyles = makeStyles((theme) => ({
  deliveryPrice: {
    whiteSpace: "nowrap",
    fontWeight: 700,
    minWidth: "80px",
    textAlign: "right",
  },
  currentPickupLocation: {
    "& h2": {
      marginRight: 0,
      fontWeight: 500,
    },
    backgroundColor: alpha(theme.palette.primary.light, 0.5),
  },
}));

const PickupDeliveryOption = ({ deliveryMethod, handleChange }) => {
  const { setDeliveryOption } = useCheckout();
  const [currentPickupLocation, setCurrentPickupLocation] = useState(() => {
    return deliveryMethod.pickupLocations.length > 0
      ? deliveryMethod.pickupLocations[0]
      : null;
  });
  const { user } = useAuth();
  const { t } = useTranslation();

  const onChange = (e) => {
    handleChange(e.target.value);
  };

  const pickupLocations = useMemo(() => {
    return deliveryMethod.pickupLocations.filter(
      (pl) => !pl.uuid.endsWith("-111111111111"),
    );
  }, [deliveryMethod]);

  return (
    <Box>
      <Formik
        initialValues={{
          name: user.name,
          emailAddress: user.emailAddress,
        }}
        validateOnMount={true}
        validationSchema={schema(t)}
        onSubmit={(values) => {
          const data = {
            contactInfo: { ...values },
          };
          setDeliveryOption(currentPickupLocation, data);
        }}
      >
        {({ submitForm, isValid, isSubmitting }) => {
          return (
            <Form>
              {pickupLocations.length > 1 && (
                <>
                  <CheckoutCardContent>
                    <CheckoutCardSubHeader>
                      <Trans>Select store</Trans>
                    </CheckoutCardSubHeader>
                    <Box>
                      <Trans>
                        Please select which store you would like to pick up
                        your order at:
                      </Trans>
                    </Box>
                  </CheckoutCardContent>
                  <List data-testid="pickup-location-list">
                    {pickupLocations.map((pickupLocation, idx) => {
                      console.log("Delivery option", pickupLocation);
                      const selected =
                        pickupLocation.uuid.substring(0, 23) ==
                        currentPickupLocation?.uuid.substring(0, 23);

                      return (
                        <ListItemRadioButton
                          key={pickupLocation.uuid}
                          onClick={() =>
                            setCurrentPickupLocation(pickupLocation)
                          }
                          primaryText={pickupLocation.pickupname}
                          secondaryText={
                            <div>
                              <div>{pickupLocation.pickupaddress1}</div>
                              <div>
                                {pickupLocation.pickuppostalcode}{" "}
                                {pickupLocation.pickupcity}
                              </div>
                            </div>
                          }
                          selected={selected}
                        />
                      );
                    })}
                  </List>
                </>
              )}

              {currentPickupLocation && (
                <PickupLocation pickupLocation={currentPickupLocation} />
              )}

              <ChooseDeliverySpeed
                pickupLocation={currentPickupLocation}
                pickupLocations={deliveryMethod.pickupLocations}
                setCurrentPickupLocation={setCurrentPickupLocation}
              />

              <CheckoutCardContent data-testid="contact-details-container">
                <CheckoutCardSubHeader>
                  <Trans>Contact details</Trans>
                </CheckoutCardSubHeader>

                <Field
                  component={FormikTextField}
                  label={t("Your name")}
                  name="name"
                />

                <Field
                  component={FormikTextField}
                  label={t("Your email address")}
                  name="emailAddress"
                />

                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                    type="submit"
                  >
                    <Trans>Next step</Trans>
                  </Button>
                </Box>
              </CheckoutCardContent>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

const ChooseDeliverySpeed = ({
  pickupLocation,
  pickupLocations,
  setCurrentPickupLocation,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const shopDeliverySpeeds = useMemo(() => {
    if (!pickupLocation) {
      return [];
    }
    return pickupLocations
      .filter(
        (pl) =>
          pl.uuid.substring(0, 23) == pickupLocation.uuid.substring(0, 23),
      )
      .sort((a, b) => b.deliveryTime.seconds - a.deliveryTime.seconds);
  }, [pickupLocation]);

  if (!pickupLocation || shopDeliverySpeeds.length == 1) {
    return null;
  }

  return (
    <>
      <CheckoutCardContent>
        <CheckoutCardSubHeader>
          <Trans>Express delivery</Trans>
        </CheckoutCardSubHeader>
      </CheckoutCardContent>
      <CheckoutCardList>
        {shopDeliverySpeeds.map((pl) => {
          let description = t("Normal delivery");
          if (pl.uuid.endsWith("-111111111111")) {
            description = t("Express delivery");
          }
          const deliveryTime = t("Pick up your order in our store in %s", {
            postProcess: "sprintf",
            sprintf: [pl.deliveryTime.string],
          });
          console.log("Delivery time seconds", pl.deliveryTime.seconds);
          const priceString =
            pl.price == 0 ? t("Free") : formatCurrency(pl.price);
          return (
            <ListItemRadioButton
              key={pl.uuid}
              onClick={() => setCurrentPickupLocation(pl)}
              primaryText={description}
              secondaryText={deliveryTime}
              selected={pl.uuid == pickupLocation.uuid}
              action={
                <Box className={classes.deliveryPrice}>{priceString}</Box>
              }
            />
          );
        })}
      </CheckoutCardList>
    </>
  );
};

const PickupLocation = ({ pickupLocation }) => {
  const classes = useStyles();
  return (
    <>
      <CheckoutCardContent>
        <CheckoutCardSubHeader>
          <Trans>Store details</Trans>
        </CheckoutCardSubHeader>
        <Box>
          <Trans>You may pick up your order in our store:</Trans>
        </Box>
      </CheckoutCardContent>
      <Box p={2} mb={2} mt={1} className={classes.currentPickupLocation}>
        <h2>
          {pickupLocation.pickupname}, {pickupLocation.pickupcity}
        </h2>
        <div>{pickupLocation.pickupaddress1}</div>
        <div>
          {pickupLocation.pickuppostalcode} {pickupLocation.pickupcity}
        </div>
      </Box>
    </>
  );
};

const schema = (t) => () => {
  const validators = getYUPValidators(t);
  return Yup.object().shape({
    name: validators.FULL_NAME.required(t("This is required!")),
    emailAddress: validators.EMAIL_ADDRESS.required(t("This is required!")),
  });
};

export default PickupDeliveryOption;
